import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { useCart } from '@/context/CartContext';
import { createOrder, createCheckoutSession } from '@/lib/api';
import SEO from '@/components/SEO';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Lock, ArrowLeft, Gift, CheckCircle, X, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getImageUrl } from '@/components/ImageUploadField';

// Redimensionne les URLs images MongoDB (?w=) — vignette 64px du récap commande.
const optimizeImg = (url, width) => {
  if (!url) return url;
  if (url.startsWith('/api/')) return getImageUrl(url, width);
  if (url.includes('images.unsplash.com')) {
    const base = url.replace(/&w=\d+/, '').replace(/&q=\d+/, '');
    return `${base}&w=${width}&q=75`;
  }
  return url;
};

function calculateShipping(items, shippingConfig) {
  if (!shippingConfig || !shippingConfig.packages) return null;
  const totalVolume = items.reduce((sum, item) => sum + (item.volume_cm3 || 0) * item.quantity, 0);
  if (totalVolume === 0) return null;
  const sorted = [...shippingConfig.packages]
    .filter(p => p.code !== 'sur_mesure')
    .sort((a, b) => (a.max_volume_cm3 || 0) - (b.max_volume_cm3 || 0));
  for (const pkg of sorted) {
    if (pkg.max_volume_cm3 && totalVolume <= pkg.max_volume_cm3) return { ...pkg, totalVolume };
  }
  const sm = shippingConfig.packages.find(p => p.code === 'sur_mesure');
  return sm ? { ...sm, totalVolume, needsQuote: sm.price_eur === 0 } : null;
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, totalPrice, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [shippingConfig, setShippingConfig] = useState(null);
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCard, setGiftCard] = useState(null);
  const [giftCardError, setGiftCardError] = useState('');
  const [checkingGiftCard, setCheckingGiftCard] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: ''
  });

  useEffect(() => {
    api.get('/shipping-config').then(r => setShippingConfig(r.data)).catch(() => {});
  }, []);

  const shipping = calculateShipping(items, shippingConfig);
  const shippingCost = shipping && !shipping?.needsQuote ? shipping.price_eur : 0;
  const giftDiscount = giftCard ? Math.min(giftCard.remaining_amount, totalPrice + shippingCost) : 0;
  const finalTotal = Math.max(0, totalPrice + shippingCost - giftDiscount);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleApplyGiftCard = async () => {
    if (!giftCardCode.trim()) return;
    setCheckingGiftCard(true);
    setGiftCardError('');
    setGiftCard(null);
    try {
      const res = await api.get(`/gift-cards/validate/${giftCardCode.trim().toUpperCase()}`);
      setGiftCard(res.data);
      toast.success(`Bon cadeau appliqué : -${Math.min(res.data.remaining_amount, totalPrice + shippingCost).toFixed(2)} €`);
    } catch (err) {
      setGiftCardError(err.response?.data?.detail || 'Code invalide');
    } finally {
      setCheckingGiftCard(false);
    }
  };

  const handleRemoveGiftCard = () => {
    setGiftCard(null);
    setGiftCardCode('');
    setGiftCardError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error('Votre panier est vide'); navigate('/commander-en-ligne'); return; }
    setLoading(true);
    try {
      const orderData = {
        items: items.map(item => ({ product_id: item.id, quantity: item.quantity })),
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone || null,
        shipping_address: formData.shipping_address,
        shipping_cost: shippingCost,
        shipping_package: shipping?.code || null,
        gift_card_code: giftCard?.code || null
      };
      const orderResponse = await createOrder(orderData);
      const order = orderResponse.data;
      const checkoutResponse = await createCheckoutSession({ order_id: order.id, origin_url: window.location.origin });
      if (checkoutResponse.data.url) window.location.href = checkoutResponse.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Erreur lors de la création de la commande');
      setLoading(false);
    }
  };

  if (items.length === 0) { navigate('/panier'); return null; }

  return (
    <PublicLayout>
      <SEO
        title="Paiement Sécurisé — Finaliser ma Commande"
        description="Finalisez votre commande Villa Marceau en toute sécurité via Stripe."
        canonical="/checkout"
        noindex={true}
      />
      <div className="py-12 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <Link to="/panier" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors" data-testid="back-to-cart">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au panier
          </Link>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-8">Finaliser la commande</h1>
          <div className="grid lg:grid-cols-[1fr_400px] gap-12">
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-card rounded-lg p-6 border border-border">
                <h2 className="font-heading text-xl font-semibold mb-6">Informations de livraison</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="customer_name">Nom complet *</Label>
                    <Input id="customer_name" name="customer_name" value={formData.customer_name} onChange={handleChange} required className="input-underline mt-2" placeholder="Jean Dupont" data-testid="input-name" />
                  </div>
                  <div>
                    <Label htmlFor="customer_email">Email *</Label>
                    <Input id="customer_email" name="customer_email" type="email" value={formData.customer_email} onChange={handleChange} required className="input-underline mt-2" placeholder="jean@example.com" data-testid="input-email" />
                  </div>
                  <div>
                    <Label htmlFor="customer_phone">Téléphone</Label>
                    <Input id="customer_phone" name="customer_phone" type="tel" value={formData.customer_phone} onChange={handleChange} className="input-underline mt-2" placeholder="06 12 34 56 78" data-testid="input-phone" />
                  </div>
                  <div>
                    <Label htmlFor="shipping_address">Adresse de livraison *</Label>
                    <Textarea id="shipping_address" name="shipping_address" value={formData.shipping_address} onChange={handleChange} required className="mt-2 min-h-[100px] resize-none" placeholder={"123 rue Example\n37000 Tours\nFrance"} data-testid="input-address" />
                  </div>
                </div>
              </div>

              {/* Bon cadeau */}
              <div className="bg-card rounded-lg p-6 border border-border">
                <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-primary" />
                  Bon cadeau
                </h2>
                {!giftCard ? (
                  <div className="flex gap-3">
                    <Input
                      value={giftCardCode}
                      onChange={e => setGiftCardCode(e.target.value.toUpperCase())}
                      placeholder="VM-XXXX-XXXX"
                      className="font-mono tracking-widest"
                      maxLength={12}
                      data-testid="checkout-gift-card-input"
                    />
                    <Button type="button" variant="outline" onClick={handleApplyGiftCard} disabled={checkingGiftCard || !giftCardCode.trim()} data-testid="apply-gift-card-btn">
                      {checkingGiftCard ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Appliquer'}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-mono text-sm font-bold">{giftCard.code}</span>
                      <span className="text-green-700 text-sm">— -{giftDiscount.toFixed(2)} €</span>
                    </div>
                    <button type="button" onClick={handleRemoveGiftCard} className="text-muted-foreground hover:text-foreground">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {giftCardError && <p className="text-sm text-destructive mt-2">{giftCardError}</p>}
              </div>

              <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="submit-order-btn">
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Traitement en cours...</>
                ) : (
                  <><Lock className="w-5 h-5 mr-2" />Payer {finalTotal.toFixed(2)} €</>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground">Paiement sécurisé par Stripe. Vos informations bancaires ne sont jamais stockées.</p>
            </form>

            {/* Order summary */}
            <div className="lg:sticky lg:top-28 lg:self-start">
              <div className="bg-muted/50 rounded-lg p-6 border border-border">
                <h2 className="font-heading text-xl font-semibold mb-6">Votre commande</h2>
                <div className="space-y-4 max-h-80 overflow-y-auto">
                  {items.map((item) => (
                    <div key={item.id} className="flex gap-4" data-testid={`checkout-item-${item.id}`}>
                      <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                        <img src={optimizeImg(item.images?.[0], 160) || 'https://images.unsplash.com/photo-1726208224138-80303abb73d3?crop=entropy&cs=srgb&fm=jpg&q=85'} alt={item.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <p className="text-muted-foreground text-sm">Qté: {item.quantity}</p>
                      </div>
                      <p className="font-semibold text-sm">{(item.price * item.quantity).toFixed(2)} €</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-border mt-6 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sous-total</span>
                    <span>{totalPrice.toFixed(2)} €</span>
                  </div>
                  {shipping && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1"><Truck className="w-3 h-3" />{shipping.label}</span>
                      <span>{shipping.needsQuote ? 'Devis' : `${shippingCost.toFixed(2)} €`}</span>
                    </div>
                  )}
                  {giftCard && giftDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-700">
                      <span className="flex items-center gap-1"><Gift className="w-3 h-3" />Bon cadeau {giftCard.code}</span>
                      <span>-{giftDiscount.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-lg pt-2 border-t border-border">
                    <span>Total</span>
                    <span className="text-primary" data-testid="checkout-total">{finalTotal.toFixed(2)} €</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
